import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import { useLinkTo, useMount } from '../../../../hooks';
import analytics from '../../../../modules/analytics';
import {
  getStatus as getSMSStatus,
  canSendClientApptOptInSms,
  ClientSMSStatus,
} from '../../../../modules/api/clients/sms';
import ClientPaymentRequestCheckout from '../../../../modules/consumer/ClientPaymentRequestCheckout';
import { IRootDispatch } from '../../../../store/models';
import { Client } from '../../../../api/Providers/Appointments';
import {
  ModalHost,
  Stack,
  HeaderText,
  Button,
} from '../../../ui';
import { ApptSmsInviteDialogContainer } from '../../appointment/ApptSmsInvite';
import RequestPaymentDisabledDialog from '../RequestPaymentDisabledDialog';
import RequestPaymentSmsDisabledDialog from '../RequestPaymentSmsDisabledDialog';
import { PRO_CLIENT_VIEW } from '../../../../route-names';

export type RequestPaymentTriggerProps = {
  onClick: () => void;
  isRequesting: boolean;
};

type RequestPaymentContainerProps = {
  client: Client;
  appointmentId: number;
  providerId: number;
  source?: string;
  renderTrigger: (props: RequestPaymentTriggerProps) => React.ReactNode;
  /** Called when payment request fails */
  onFailed?: () => void;
  /** Called when the payment request has been sent and is pending */
  onRequesting?: () => void;
  /** Called when the payment request succeeds */
  onSent?: () => void;
};

const PaymentRequestSnackbarContent = ({
  title, onConfirm, confirmText,
}) => (
  <Stack space={8}>
    <Box maxWidth={255}>
      <HeaderText level={4}>
        {title}
      </HeaderText>
    </Box>
    <Inline justifyContent="flex-end">
      <Button
        appearance="primary-neue"
        size="small"
        onClick={onConfirm}
      >
        {confirmText}
      </Button>
    </Inline>
  </Stack>
);

/**
 * Encapsulates business logic for determining which dialog(s) to display when payment request is
 * sent, as well as sending the payment request.
 */
export const RequestPaymentContainer: React.FC<RequestPaymentContainerProps> = ({
  client,
  appointmentId,
  providerId,
  source,
  renderTrigger,
  onFailed,
  onRequesting,
  onSent,
}) => {
  const linkTo = useLinkTo();
  const [showSMSDisabled, setShowSMSDisabled] = useState<boolean>(false);
  const [showSMSNotConfigured, setShowSMSNotConfigured] = useState<boolean>(false);
  const [showSMSInvite, setShowSMSInvite] = useState<boolean>(false);
  const [smsStatus, setSmsStatus] = useState<ClientSMSStatus>(
    ClientSMSStatus.NoSmsPermissionHistory,
  );
  const [isRequesting, setRequesting] = useState<boolean>(false);
  const clientHasNoNumber = !client?.sms_status?.mobile_phone_number;
  const dispatch = useDispatch<IRootDispatch>();

  useMount(async () => {
    const currentSmsStatus = getSMSStatus(await canSendClientApptOptInSms(String(client.id)));
    setSmsStatus(currentSmsStatus);
  });

  const handleDisabledConfirm = () => {
    setShowSMSDisabled(false);
  };

  const handleNotConfiguredConfirm = () => {
    setShowSMSNotConfigured(false);
    linkTo(PRO_CLIENT_VIEW, { clientId: client.id, providerId });
  };

  const handleInviteDismiss = () => {
    setShowSMSInvite(false);
  };

  const handleTriggerClick = useCallback(() => {
    const trackingParams = {
      appointment_id: appointmentId,
      provider_id: providerId,
      client_id: client.id,
      client_sms_opt_in: !client.sms_status?.appointment_sms_opted_out,
      source,
    };
    analytics.track('pro_request_payment_button_clicked', trackingParams);
    if (smsStatus === ClientSMSStatus.ClientSmsDisabled) {
      // if client specifically opts out of SMS
      setShowSMSDisabled(true);
    } else if (
      smsStatus === ClientSMSStatus.NoSmsPermissionHistory
        || smsStatus === ClientSMSStatus.SmsInvitePending
        || clientHasNoNumber
    ) {
      // if client has not opted out of SMS or set it
      // or client does not have a number at all
      if (clientHasNoNumber) {
        setShowSMSNotConfigured(true);
      } else {
        setShowSMSInvite(true);
      }
    } else if (!isRequesting) {
      setRequesting(true);
      onRequesting?.();
      ClientPaymentRequestCheckout.sendRequestForPayment(providerId, appointmentId)
        .then(
          () => {
            setRequesting(false);
            analytics.track('pro_request_payment_sms_sent', trackingParams);
            // Show success snackbar
            dispatch.snackbar.open({
              mainContent: (
                <PaymentRequestSnackbarContent
                  title={`Payment request successfully sent to ${client.sms_status?.mobile_phone_number?.national || ''}`}
                  onConfirm={() => dispatch.snackbar.close()}
                  confirmText="Got It"
                />
              ),
              icon: 'success-icon',
              sideColor: colors.green,
            });
            onSent?.();
          },
          () => {
            setRequesting(false);
            analytics.track('pro_request_payment_sms_failed', trackingParams);
            // Show error snackbar
            dispatch.snackbar.open({
              mainContent: (
                <PaymentRequestSnackbarContent
                  title={`Payment request failed to send to ${client.sms_status?.mobile_phone_number?.national || ''}`}
                  onConfirm={() => dispatch.snackbar.close()}
                  confirmText="Dismiss"
                />
              ),
              sideColor: colors.red,
              icon: 'error-icon',
            });
            onFailed?.();
          },
        );
    }
  }, [
    clientHasNoNumber,
    smsStatus,
    appointmentId,
    client.id,
    client.sms_status,
    isRequesting,
    providerId,
    source,
    onFailed,
    onSent,
    onRequesting,
    dispatch.snackbar,
  ]);

  const requestPaymentTrigger = useMemo(
    () => renderTrigger({ onClick: handleTriggerClick, isRequesting }),
    [
      renderTrigger,
      handleTriggerClick,
      isRequesting,
    ],
  );

  return (
    <ModalHost>
      {requestPaymentTrigger}
      {showSMSDisabled && (
        <RequestPaymentSmsDisabledDialog onConfirm={handleDisabledConfirm} />
      )}
      {showSMSNotConfigured && (
        <RequestPaymentDisabledDialog onConfirm={handleNotConfiguredConfirm} />
      )}
      {showSMSInvite && (
        <ApptSmsInviteDialogContainer
          client={client}
          trackingPrefix="pro_request_payment"
          source={source}
          onInviteCancel={handleInviteDismiss}
          onInviteFailed={handleInviteDismiss}
          onInviteSent={handleInviteDismiss}
        />
      )}
    </ModalHost>
  );
};
